import type { Prospect } from "../types";

// Public comps-proxy Worker (Repliers + Airtable). CORS allows dealengine-crm.pages.dev.
export const WORKER_URL =
  "https://comps-proxy.brunorealestategroup.workers.dev";

const SOURCE_TABLE: Record<string, string> = {
  "DealEngine auction": "Auction Notices",
  "DealEngine probate": "Probate Leads",
};

const lienPos = (v: unknown): number | undefined => {
  const m = /(\d+)/.exec(String(v ?? ""));
  return m ? Number(m[1]) : undefined;
};
const phonesFrom = (vals: unknown[]) =>
  vals
    .filter(Boolean)
    .map((number) => ({ number: String(number), type: "Work" as const }));

/** Map a full Airtable auction/probate record to prospect fields (latest values). */
export const mapRecord = (
  f: Record<string, any>,
  source: string,
): Partial<Prospect> => {
  if (source === "DealEngine probate") {
    return {
      owner_name: f["Borrower / Owner"] || f["Decedent"] || f["Name"],
      property_address: f["Final Address"] || f["Address"],
      property_city: f["City"],
      property_state: f["State"],
      property_zip: f["Zip"] != null ? String(f["Zip"]) : undefined,
      county: f["County"],
      avm: f["ARV Estimate"] || f["Est Value"],
      deal_tier: f["Deal Tier v3"],
      legal_description: f["Legal Description"],
      phones: phonesFrom([f["Phone"], f["Phone 2"], f["Phone 3"]]),
    };
  }
  const equity =
    f["ARV Estimate"] && f["Equity %"]
      ? Math.round(f["ARV Estimate"] * f["Equity %"])
      : undefined;
  return {
    owner_name: f["Borrower / Owner"],
    property_address: f["Final Address"] || f["Address"],
    property_city: f["City"],
    property_state: f["State"],
    property_zip: f["Zip"] != null ? String(f["Zip"]) : undefined,
    county: f["County"],
    beds: f["Beds"],
    baths: f["Baths"],
    sqft: f["Sqft"],
    year_built: f["Year Built"],
    list_price: f["Last List Price"],
    sold_amount: f["Last Sold Price"],
    sold_date: f["Last Sold Date"],
    list_date: f["Last List Date"],
    avm: f["ARV Estimate"],
    deal_tier: f["Deal Tier v3"],
    estimated_equity: equity,
    opening_bid: f["Opening Bid"],
    auction_date: f["Sale Date"],
    auction_type: f["Auction Type"],
    legal_description: f["Legal Description"],
    lien_amount: f["Total Known Liens"],
    lien_position: lienPos(f["Lien Position"]),
    plaintiff_lender: f["Plaintiff / Lender"],
    trustee: f["Trustee"],
    tax_amount: f["Tax Amount Owed"],
    phones: phonesFrom([f["PHONE "], f["PHONE 2"], f["PHONE 3"], f["PHONE 4"]]),
  };
};

export const isEmpty = (v: unknown) =>
  v == null || v === "" || (Array.isArray(v) && v.length === 0);

/**
 * Fetch the latest Airtable source record and return a prospect patch.
 * Phones are always refreshed (the async skip-trace result); every other field
 * is only filled when the current prospect value is empty, so user edits stay intact.
 * Returns null if the prospect has no source link or the record can't be fetched.
 */
export async function buildRefreshPatch(
  prospect: Prospect,
): Promise<Partial<Prospect> | null> {
  const table = prospect.source ? SOURCE_TABLE[prospect.source] : undefined;
  if (!table || !prospect.source_record_id) return null;

  const res = await fetch(`${WORKER_URL}/airtable/query`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      table,
      maxRecords: 1,
      filterByFormula: `RECORD_ID()="${prospect.source_record_id}"`,
    }),
  });
  if (!res.ok) throw new Error(`Worker ${res.status}`);
  const data = await res.json();
  const fields = data?.records?.[0]?.fields;
  if (!fields) return null;

  const incoming = mapRecord(fields, prospect.source as string);
  const patch: Record<string, unknown> = {};

  // Always refresh phones if skip trace produced any.
  if (Array.isArray(incoming.phones) && incoming.phones.length > 0) {
    patch.phones = incoming.phones;
  }
  // Fill only currently-empty fields for everything else (preserve user edits).
  for (const [key, value] of Object.entries(incoming)) {
    if (key === "phones") continue;
    if (isEmpty(value)) continue;
    if (isEmpty((prospect as Record<string, unknown>)[key])) patch[key] = value;
  }
  return patch;
}
